/**
 * HistoryAccordion Component
 * 본사 입출금 내역 아코디언 (협력사별/라이더별 그룹화)
 */
import React, { useState, useMemo, useEffect } from 'react';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { formatAmount } from '../lib/amountFormatterHelpers';
import { HeadWithdrawHistory } from '../types/headWithdraw.types';
import {
    getTransactionLabel,
    getBadgeClass
} from './withdrawHistory.helpers';

/** 페이지당 항목 수 */
const ITEMS_PER_PAGE = 15;

/** 뷰 모드: 협력사별 또는 라이더별 */
export type ViewMode = 'supplier' | 'rider';

/** 그룹화된 데이터 타입 */
interface GroupedData {
    id: string;
    name: string;
    histories: HeadWithdrawHistory[];
    totalDeposit: number;
    totalWithdrawal: number;
    currentBalance: number | null;
}

interface HistoryAccordionProps {
    /** 전체 내역 목록 */
    histories: HeadWithdrawHistory[];
    /** 뷰 모드 (협력사별 또는 라이더별) */
    viewMode: ViewMode;
    /** 뷰 모드 변경 핸들러 */
    onViewModeChange: (mode: ViewMode) => void;
    /** 협력사 사용자 여부 */
    isSupplierUser?: boolean;
    /** 협력사 사용자의 협력사 ID */
    userSuppId?: string;
    /** 라이더별 현재 잔액 맵 (백엔드에서 계산된 값) */
    currentBalanceByRider?: Record<string, number>;
    /** 협력사별 현재 잔액 맵 (백엔드에서 계산된 값) */
    currentBalanceBySupplier?: Record<string, number>;
}

const HistoryAccordion: React.FC<HistoryAccordionProps> = ({
    histories,
    viewMode,
    onViewModeChange,
    isSupplierUser = false,
    userSuppId,
    currentBalanceByRider,
    currentBalanceBySupplier
}) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [groupPages, setGroupPages] = useState<Record<string, number>>({});
    const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

    /**
     * 날짜 문자열을 Date 객체로 변환
     */
    const parseDateTime = (dateStr: string | undefined): Date => {
        if (!dateStr) return new Date(0);
        return new Date(dateStr);
    };

    /**
     * viewMode에 따라 데이터 필터링
     * - 협력사 모드: reqType === 'S' (ACCT_TARGET='S')
     * - 라이더 모드: reqType === 'R' (ACCT_TARGET='R')
     */
    const filteredHistories = useMemo((): HeadWithdrawHistory[] => {
        if (viewMode === 'supplier') {
            return histories.filter(h => h.reqType === 'S');
        } else {
            return histories.filter(h => h.reqType === 'R');
        }
    }, [histories, viewMode]);

    /**
     * 데이터를 협력사 또는 라이더별로 그룹화
     */
    const groupedData = useMemo((): GroupedData[] => {
        const groupMap = new Map<string, GroupedData>();

        // suppId -> suppName 매핑을 위한 맵 (협력사 모드에서 사용)
        const suppIdToNameMap = new Map<string, string>();

        filteredHistories.forEach((history) => {
            let groupId: string;
            let groupName: string;
            let suppIdForBalance: string | undefined;

            if (viewMode === 'supplier') {
                groupId = history.suppName || '미지정';
                groupName = history.suppName || '미지정';
                // suppId -> suppName 매핑 저장 (협력사 ID로 잔액 조회를 위해)
                if (history.suppName) {
                    // suppName으로 그룹화하지만, 백엔드 잔액 맵은 suppId 기준
                    // 첫 번째 history에서 suppId가 있으면 매핑 저장
                    // (협력사 모드에서는 suppId 기준으로 잔액 조회)
                    suppIdForBalance = undefined; // 협력사 모드는 suppName으로 그룹화하므로 별도 처리
                }
            } else {
                groupId = history.riderId || '미지정';
                groupName = history.riderName
                    ? `${history.riderName}(${history.riderId || '-'})`
                    : history.riderId || '미지정';
            }

            if (!groupMap.has(groupId)) {
                groupMap.set(groupId, {
                    id: groupId,
                    name: groupName,
                    histories: [],
                    totalDeposit: 0,
                    totalWithdrawal: 0,
                    currentBalance: null
                });
            }

            const group = groupMap.get(groupId)!;
            group.histories.push(history);

            if (history.amount > 0) {
                group.totalDeposit += history.amount;
            } else {
                group.totalWithdrawal += Math.abs(history.amount);
            }
        });

        // 각 그룹 처리: 정렬 및 현재 잔액 설정
        const result = Array.from(groupMap.values()).map(group => {
            // 처리일자 기준 오름차순 정렬 (오래된 순) - 테이블 표시용
            group.histories.sort((a, b) => {
                const dateA = parseDateTime(a.processDateTime);
                const dateB = parseDateTime(b.processDateTime);
                return dateA.getTime() - dateB.getTime();
            });

            // 현재 잔액: 백엔드에서 제공하는 잔액 맵 사용
            // (백엔드에서 ACCT_DATE DESC, SEQ_NO DESC 정렬하여 계산)
            if (viewMode === 'rider' && currentBalanceByRider) {
                // 라이더 모드: group.id가 riderId
                const riderId = group.id;
                if (riderId && riderId !== '미지정') {
                    const balance = currentBalanceByRider[riderId];
                    group.currentBalance = balance !== undefined ? balance : null;
                }
            } else if (viewMode === 'supplier' && currentBalanceBySupplier) {
                // 협력사 모드: group.id가 suppName이므로, history에서 suppId를 찾아야 함
                // 그룹의 첫 번째 history에서 suppId 추출 (같은 그룹은 같은 suppId를 가짐)
                const firstHistory = group.histories.find(h => h.reqType === 'S' && h.suppId);
                if (firstHistory && firstHistory.suppId) {
                    const balance = currentBalanceBySupplier[firstHistory.suppId];
                    group.currentBalance = balance !== undefined ? balance : null;
                } else {
                    // suppId가 없으면 폴백: 프론트엔드 계산 사용
                    const sortedByDateDesc = [...group.histories]
                        .filter(h => h.acctType !== 'RD' && h.acctType !== 'SD')
                        .sort((a, b) => {
                            const acctDateA = a.acctDate || '';
                            const acctDateB = b.acctDate || '';
                            const acctDateCompare = acctDateB.localeCompare(acctDateA);
                            if (acctDateCompare !== 0) return acctDateCompare;

                            const dateA = parseDateTime(a.processDateTime);
                            const dateB = parseDateTime(b.processDateTime);
                            const dateCompare = dateB.getTime() - dateA.getTime();
                            if (dateCompare !== 0) return dateCompare;

                            return b.no - a.no;
                        });

                    const latestWithBalAmt = sortedByDateDesc.find(h => h.balAmt !== undefined && h.balAmt !== null);
                    group.currentBalance = latestWithBalAmt
                        ? (latestWithBalAmt.balAmt ?? 0) - (latestWithBalAmt.feeAmt ?? 0)
                        : null;
                }
            } else {
                // 잔액 맵이 없는 경우 폴백: 기존 프론트엔드 계산 로직 사용
                const sortedByDateDesc = [...group.histories]
                    .filter(h => h.acctType !== 'RD' && h.acctType !== 'SD')
                    .sort((a, b) => {
                        const acctDateA = a.acctDate || '';
                        const acctDateB = b.acctDate || '';
                        const acctDateCompare = acctDateB.localeCompare(acctDateA);
                        if (acctDateCompare !== 0) return acctDateCompare;

                        const dateA = parseDateTime(a.processDateTime);
                        const dateB = parseDateTime(b.processDateTime);
                        const dateCompare = dateB.getTime() - dateA.getTime();
                        if (dateCompare !== 0) return dateCompare;

                        return b.no - a.no;
                    });

                const latestWithBalAmt = sortedByDateDesc.find(h => h.balAmt !== undefined && h.balAmt !== null);
                group.currentBalance = latestWithBalAmt
                    ? (latestWithBalAmt.balAmt ?? 0) - (latestWithBalAmt.feeAmt ?? 0)
                    : null;
            }

            return group;
        });

        return result.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    }, [filteredHistories, viewMode, currentBalanceByRider, currentBalanceBySupplier]);

    /**
     * 협력사 사용자일 때 협력사 모드에서 자동으로 아코디언 펼침
     */
    useEffect(() => {
        if (isSupplierUser && viewMode === 'supplier' && groupedData.length > 0 && !hasAutoExpanded) {
            // 협력사 사용자의 경우 자신의 협력사 아코디언을 자동으로 펼침
            const allIds = new Set(groupedData.map(group => group.id));
            setExpandedGroups(allIds);
            setHasAutoExpanded(true);
        }
    }, [isSupplierUser, viewMode, groupedData, hasAutoExpanded]);

    /**
     * 그룹 아코디언 토글
     */
    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    /**
     * 전체 펼치기
     */
    const expandAll = () => {
        const allIds = new Set(groupedData.map(group => group.id));
        setExpandedGroups(allIds);
    };

    /**
     * 전체 접기
     */
    const collapseAll = () => {
        setExpandedGroups(new Set());
    };

    /**
     * 전체 펼침 여부 확인
     */
    const isAllExpanded = groupedData.length > 0 && expandedGroups.size === groupedData.length;

    /**
     * 그룹별 페이지 변경
     */
    const handleGroupPageChange = (groupId: string, page: number) => {
        setGroupPages(prev => ({
            ...prev,
            [groupId]: page
        }));
    };

    /**
     * 그룹별 현재 페이지의 아이템 가져오기
     */
    const getGroupCurrentItems = (group: GroupedData) => {
        const currentPage = groupPages[group.id] || 1;
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return group.histories.slice(startIndex, endIndex);
    };

    /**
     * 그룹별 총 페이지 수
     */
    const getGroupTotalPages = (group: GroupedData) => {
        return Math.ceil(group.histories.length / ITEMS_PER_PAGE);
    };

    /**
     * 금액 포맷팅
     */
    const formatCurrency = (amount: number): string => {
        return amount.toLocaleString('ko-KR') + '원';
    };

    if (histories.length === 0 || filteredHistories.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="view-mode-toggle">
                        <button
                            className={`toggle-btn ${viewMode === 'supplier' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('supplier')}
                        >
                            <i className="fas fa-building" />
                            협력사
                        </button>
                        <button
                            className={`toggle-btn ${viewMode === 'rider' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('rider')}
                        >
                            <i className="fas fa-motorcycle" />
                            라이더
                        </button>
                    </div>
                    <div className="empty-state">
                        <p>조회된 내역이 없습니다.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                {/* 뷰 모드 토글 + 전체 펼치기/접기 */}
                <div className="accordion-header-controls">
                    <div className="view-mode-toggle">
                        <button
                            className={`toggle-btn ${viewMode === 'supplier' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('supplier')}
                        >
                            <i className="fas fa-building" />
                            협력사
                        </button>
                        <button
                            className={`toggle-btn ${viewMode === 'rider' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('rider')}
                        >
                            <i className="fas fa-motorcycle" />
                            라이더
                        </button>
                    </div>
                    <button
                        className="accordion-toggle-btn"
                        onClick={isAllExpanded ? collapseAll : expandAll}
                    >
                        <i className={`fas fa-${isAllExpanded ? 'compress-alt' : 'expand-alt'}`}></i>
                        {isAllExpanded ? '전체 접기' : '전체 펼치기'}
                    </button>
                </div>

                {/* 아코디언 목록 */}
                <div className="history-accordion">
                    {groupedData.map((group) => {
                        const isExpanded = expandedGroups.has(group.id);
                        const currentItems = getGroupCurrentItems(group);
                        const totalPages = getGroupTotalPages(group);
                        const currentPage = groupPages[group.id] || 1;

                        return (
                            <div key={group.id} className="accordion-item">
                                {/* 그룹 헤더 */}
                                <div
                                    className="accordion-header"
                                    onClick={() => toggleGroup(group.id)}
                                >
                                    <span className="group-name">{group.name}</span>
                                    <div className="accordion-header-right">
                                        <div className="group-info-cards">
                                            <div className="info-card">
                                                <span className="info-label">현재 잔액</span>
                                                <span className="info-value balance">
                                                    {group.currentBalance !== null
                                                        ? formatCurrency(group.currentBalance)
                                                        : '-'}
                                                </span>
                                            </div>
                                            <div className="info-card">
                                                <span className="info-label">총 입금</span>
                                                <span className="info-value deposit">
                                                    {formatCurrency(group.totalDeposit)}
                                                </span>
                                            </div>
                                            <div className="info-card">
                                                <span className="info-label">총 출금</span>
                                                <span className="info-value withdrawal">
                                                    {formatCurrency(group.totalWithdrawal)}
                                                </span>
                                            </div>
                                        </div>
                                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                    </div>
                                </div>

                                {/* 그룹 내용 (테이블) */}
                                {isExpanded && (
                                    <div className="accordion-content">
                                        <div className="table-responsive">
                                            <table className="history-table">
                                                <thead>
                                                    <tr>
                                                        <th className="col-no">NO</th>
                                                        <th className="col-date">처리일자</th>
                                                        <th className="col-type">구분</th>
                                                        <th className="col-content">내용</th>
                                                        {viewMode === 'rider' && (
                                                            <th className="col-partner">협력사명</th>
                                                        )}
                                                        <th className="col-amount">요청금액</th>
                                                        <th className="col-amount">수수료</th>
                                                        <th className="col-amount">잔액</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentItems.map((history, index) => {
                                                        const isSettlement = history.acctType === 'SW' || history.acctType === 'RW' || history.acctType === 'RP';

                                                        return (
                                                            <tr key={`${history.no}-${index}`}>
                                                                <td className="col-no">
                                                                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                                                </td>
                                                                <td className="col-date">{history.processDateTime || '-'}</td>
                                                                <td className="col-type">
                                                                    <span className={`badge ${getBadgeClass(history)}`}>
                                                                        {getTransactionLabel(history)}
                                                                    </span>
                                                                </td>
                                                                <td className="col-content">{'-'}</td>
                                                                {viewMode === 'rider' && (
                                                                    <td className="col-partner">{history.suppName || '-'}</td>
                                                                )}
                                                                <td className={`col-amount ${
                                                                    history.amount < 0 ? 'amount-withdrawal' : 'amount-deposit'
                                                                }`}>
                                                                    {history.acctType === 'W'
                                                                        ? formatAmount(history.amount - (history.feeAmt || 0))
                                                                        : formatAmount(history.amount)
                                                                    }
                                                                </td>
                                                                <td className="col-amount">
                                                                    {isSettlement ? '-' : (history.feeAmt !== undefined ? formatAmount(history.feeAmt) : '-')}
                                                                </td>
                                                                <td className="col-amount">
                                                                    {history.balAmt !== undefined && history.feeAmt !== undefined
                                                                        ? formatAmount(history.balAmt - history.feeAmt)
                                                                        : (history.balAmt !== undefined ? formatAmount(history.balAmt) : '-')
                                                                    }
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* 그룹별 페이지네이션 */}
                                        {totalPages > 1 && (
                                            <div className="pagination-container">
                                                <Pagination
                                                    currentPage={currentPage}
                                                    totalPages={totalPages}
                                                    onPageChange={(page) => handleGroupPageChange(group.id, page)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default HistoryAccordion;
